import React, { useEffect, useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  IconButton,
  Typography,
  Chip,
  Paper,
  Avatar,
  Divider,
  Stack,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Close, Person, AttachMoney, Email, Phone } from '@mui/icons-material';
import api from '../../utils/api';
import { Client, Property } from '../../types';

interface PropertyMatchingClientsDialogProps {
  open: boolean;
  onClose: () => void;
  property: Property | null;
}

const PropertyMatchingClientsDialog: React.FC<PropertyMatchingClientsDialogProps> = ({
  open,
  onClose,
  property,
}) => {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(false);

  const loadMatchingClients = useCallback(async () => {
    if (!property) return;

    setLoading(true);
    try {
      // Build query parameters based on property characteristics
      const params: any = {
        'isActive': true,
      };

      // Add price range filters to find clients whose budget matches the property price
      params.maxBudgetGte = property.price; // maxBudget >= property.price
      params.minBudgetLte = property.price; // minBudget <= property.price

      // Add property type filter
      params['interestedInPropertyType'] = property.propertyType;

      // Add payment method filter
      if (property.paymentMethod && property.paymentMethod.length > 0) {
        params['paymentMethod[]'] = property.paymentMethod;
      }

      // // Add property age (building age) filter
      // if (property.propertyAge) {
      //   params.buildingAge = property.propertyAge;
      // }

      // Add bedrooms filter
      if (property.bedrooms) {
        params.bedrooms = property.bedrooms;
      }

      // Add bathrooms filter
      if (property.bathrooms) {
        params.bathrooms = property.bathrooms;
      }

      // Add size filter
      if (property.size) {
        params.area = property.size;
      }

      // Add transaction type filter
      if (property.transactionType) {
        params.transactionType = property.transactionType;
      }
      // Location filters - strict district match
      if (property.governorate) {
        params['governorate[]'] = [property.governorate];
      }
      if (property.district) {
        params['district[]'] = [property.district];
      }
      const response = await api.get('/clients', { params });
      setClients(response.data);
    } catch (error) {
      console.error('Error loading clients:', error);
      setClients([]);
    } finally {
      setLoading(false);
    }
  }, [property]);

  useEffect(() => {
    if (open && property) {
      loadMatchingClients();
    }
  }, [open, property, loadMatchingClients]);

  const columns: GridColDef[] = [
    {
      field: 'fullName',
      headerName: 'Client',
      flex: 2,
      minWidth: 220,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
          <Avatar sx={{ bgcolor: 'primary.main', width: 44, height: 44 }}>
            <Person />
          </Avatar>
          <Box>
            <Typography variant="body2" fontWeight={600}>
              {params.row.firstName} {params.row.lastName}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Email sx={{ fontSize: 12 }} />
              {params.row.email}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: 'phone',
      headerName: 'Phone',
      flex: 1,
      minWidth: 140,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Phone sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Typography variant="body2">{params.row.phone}</Typography>
        </Box>
      ),
    },
    {
      field: 'budget',
      headerName: 'Budget Range',
      flex: 1.5,
      minWidth: 180,
      renderCell: (params) => {
        if (params.row.minBudget && params.row.maxBudget) {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <AttachMoney sx={{ fontSize: 16, color: 'success.main' }} />
              <Typography variant="body2" color="success.main" fontWeight={600}>
                ${params.row.minBudget.toLocaleString()} - ${params.row.maxBudget.toLocaleString()}
              </Typography>
            </Box>
          );
        } else if (params.row.minBudget) {
          return `From $${params.row.minBudget.toLocaleString()}`;
        } else if (params.row.maxBudget) {
          return `Up to $${params.row.maxBudget.toLocaleString()}`;
        } else if (params.row.budget) {
          return `$${params.row.budget.toLocaleString()}`;
        }
        return 'N/A';
      },
    },
    {
      field: 'propertyTypes',
      headerName: 'Interested In',
      flex: 1.5,
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {params.row.propertyTypes && params.row.propertyTypes.length > 0 ? (
            params.row.propertyTypes.map((type: string) => (
              <Chip 
                key={type} 
                label={type} 
                size="small" 
                color={type === property?.propertyType ? 'primary' : 'default'}
                variant={type === property?.propertyType ? 'filled' : 'outlined'}
              />
            ))
          ) : (
            <Typography variant="caption" color="text.secondary">All Types</Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'agent',
      headerName: 'Agent',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        params.row.agent?.user
          ? `${params.row.agent.user.firstName} ${params.row.agent.user.lastName}`
          : <Chip label="Unassigned" size="small" variant="outlined" />
      ),
    },
  ];

  const getPriceDisplay = () => {
    if (!property) return 'N/A';
    return `$${property.price.toLocaleString()}`;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: '80vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 2,
          background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
          color: 'white',
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Interested Clients
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
            {property && property.title}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            color: 'white',
            '&:hover': {
              bgcolor: 'rgba(255, 255, 255, 0.1)',
            },
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {/* Property Summary */}
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            mb: 3,
            bgcolor: 'background.default',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="subtitle2" color="text.secondary" gutterBottom fontWeight={600}>
            PROPERTY DETAILS
          </Typography>
          <Divider sx={{ my: 1.5 }} />
          <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                Property Type
              </Typography>
              <Chip
                label={property?.propertyType}
                size="small"
                color="primary"
                sx={{ mt: 0.5, fontWeight: 600 }}
              />
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                Price
              </Typography>
              <Typography variant="body1" fontWeight={600} color="success.main">
                {getPriceDisplay()}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                Location
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {property?.city}, {property?.country}
              </Typography>
            </Box>
            {property?.bedrooms && (
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Bedrooms
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {property.bedrooms}
                </Typography>
              </Box>
            )}
            {property?.bathrooms && (
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Bathrooms
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {property.bathrooms}
                </Typography>
              </Box>
            )}
            {property?.size && (
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Size
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {property.size} m²
                </Typography>
              </Box>
            )}
            {property?.propertyAge && (
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Building Age
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {property.propertyAge} years
                </Typography>
              </Box>
            )}
            {property?.paymentMethod && (
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Payment Method
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {property.paymentMethod}
                </Typography>
              </Box>
            )}
            {property?.transactionType && (
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Transaction Type
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {property.transactionType}
                </Typography>
              </Box>
            )}
            {property?.governorate && (
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Governorate
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {property.governorate}
                </Typography>
              </Box>
            )}
            {property?.district && (
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  District
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {property.district}
                </Typography>
              </Box>
            )}
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                Matching Clients
              </Typography>
              <Typography variant="body1" fontWeight={600} color="primary.main">
                {clients.length} {clients.length === 1 ? 'Client' : 'Clients'}
              </Typography>
            </Box>
          </Stack>
        </Paper>

        {/* Clients Table */}
        <Paper
          elevation={2}
          sx={{
            borderRadius: 2,
            overflow: 'hidden',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
          }}
        >
          <DataGrid
            rows={clients}
            columns={columns}
            loading={loading}
            pageSizeOptions={[10, 25, 50]}
            initialState={{
              pagination: { paginationModel: { pageSize: 10 } },
            }}
            disableRowSelectionOnClick
            autoHeight
            sx={{
              border: 'none',
              minWidth: '100%',
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#f8f9fa',
                minHeight: '56px !important',
                maxHeight: '56px !important',
              },
              '& .MuiDataGrid-columnHeader': {
                backgroundColor: '#f8f9fa',
                fontWeight: 700,
                fontSize: { xs: '0.8rem', sm: '0.875rem', md: '0.9rem' },
                padding: '0 8px',
                '& .MuiDataGrid-columnHeaderTitle': {
                  fontWeight: 700,
                  overflow: 'visible',
                  whiteSpace: 'normal',
                  lineHeight: '1.2',
                },
              },
              '& .MuiDataGrid-columnHeaderTitleContainer': {
                padding: '8px 0',
              },
              '& .MuiDataGrid-cell': {
                borderColor: '#f0f0f0',
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                padding: { xs: '4px', sm: '8px' },
              },
              '& .MuiDataGrid-cell:focus': {
                outline: 'none',
              },
              '& .MuiDataGrid-cell:focus-within': {
                outline: 'none',
              },
              '& .MuiDataGrid-row:hover': {
                backgroundColor: '#fff5f5',
                cursor: 'pointer',
              },
              '& .MuiDataGrid-row': {
                minHeight: { xs: '70px', sm: '80px' },
                maxHeight: 'fit-content',
              },
              '& .MuiDataGrid-virtualScroller': {
                overflowX: 'auto',
              },
            }}
          />
        </Paper>

        {clients.length === 0 && !loading && (
          <Box
            sx={{
              textAlign: 'center',
              py: 8,
              px: 3,
            }}
          >
            <Person sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Matching Clients Found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              There are currently no clients interested in this type of property or within this price range.
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PropertyMatchingClientsDialog;
